import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useWebSocket } from '../../hooks/useWebSocket';
import TemplatePreview from '../templates/TemplatePreview';
import { countVars, STATUS_STYLE } from '../templates/templateUtils';

/**
 * Plantillas de WhatsApp: lista, sincronización con Meta, editor y borrado (solo admin).
 */
export default function TemplatesPanel() {
  const { apiFetch } = useApi();
  const [list, setList] = useState([]);
  const [options, setOptions] = useState(null);
  const [editing, setEditing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [l, o] = await Promise.all([apiFetch('/api/templates'), apiFetch('/api/templates/options')]);
      setList(l);
      setOptions(o);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  // Meta aprobó o rechazó una plantilla (N4 o sincronización automática)
  useWebSocket(useCallback((msg) => {
    if (msg.type === 'template_status_changed') load();
  }, [load]));

  async function sync() {
    setSyncing(true);
    setNotice('');
    try {
      const r = await apiFetch('/api/templates/sync', { method: 'POST' });
      setNotice(`Sincronizado con Meta: ${r.total} plantillas (${r.created} nuevas, ${r.updated} actualizadas${r.missing ? `, ${r.missing} ya no están en Meta` : ''}).`);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncing(false);
    }
  }

  async function remove(t) {
    if (!window.confirm(`¿Borrar la plantilla "${t.name}" (${t.language}) en Meta y en el portal? No se puede deshacer.`)) return;
    try {
      await apiFetch(`/api/templates/${t.id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      alert('No se pudo borrar: ' + e.message);
    }
  }

  const managed = options?.management_enabled;

  return (
    <div style={s.wrap}>
      <div style={s.toolbar}>
        <button style={s.primaryBtn} onClick={() => setEditing(true)} disabled={!managed || editing}>+ Nueva plantilla</button>
        <button style={s.btn} onClick={sync} disabled={!managed || syncing}>{syncing ? 'Sincronizando…' : '🔄 Sincronizar con Meta'}</button>
        {!managed && options && <span style={s.muted}>Configura WA_WABA_ID en el backend para gestionar plantillas.</span>}
      </div>
      {notice && <div style={s.okBox}>{notice}</div>}
      {error && <div style={s.errorBox}>⚠️ {error}</div>}

      {editing && options && (
        <TemplateEditor
          options={options}
          onCancel={() => setEditing(false)}
          onCreated={(t) => { setEditing(false); setNotice(`"${t.name}" se envió a Meta. Queda en revisión (minutos a horas).`); load(); }}
        />
      )}

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Nombre</th><th style={s.th}>Idioma</th><th style={s.th}>Categoría</th>
              <th style={s.th}>Estado</th><th style={s.th}>Mensaje</th><th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td style={s.td} colSpan={6}>Sin plantillas. Crea una o sincroniza con Meta para traer las que ya existen.</td></tr>}
            {list.map(t => {
              const st = STATUS_STYLE[t.status] || { color: 'var(--text-2)', label: t.status };
              return (
                <tr key={t.id}>
                  <td style={s.td}><code>{t.name}</code></td>
                  <td style={s.td}>{t.language}</td>
                  <td style={s.td}>{t.category}</td>
                  <td style={s.td}>
                    <span style={{ ...s.chip, color: st.color, borderColor: st.color + '60' }}>{st.label}</span>
                    {t.rejected_reason && <div style={s.reason}>{t.rejected_reason}</div>}
                    {!t.sendable && <div style={s.reason}>No enviable desde el portal ({t.unsupported.join(', ')})</div>}
                    {t.header?.format === 'IMAGE' && !t.header_image && (
                      <div style={s.reason}>Cárgale la imagen en Administración → Promociones → Imágenes de plantillas</div>
                    )}
                  </td>
                  <td style={{ ...s.td, maxWidth: 360, color: 'var(--text-2)' }}>{t.body.text.slice(0, 120)}{t.body.text.length > 120 ? '…' : ''}</td>
                  <td style={s.td}>{managed && <button style={s.dangerBtn} onClick={() => remove(t)}>Borrar</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TemplateEditor({ options, onCancel, onCreated }) {
  const { apiFetch } = useApi();
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('es');
  const [category, setCategory] = useState('UTILITY');
  const [header, setHeader] = useState('');
  const [headerExample, setHeaderExample] = useState('');
  const [body, setBody] = useState('');
  const [bodyExamples, setBodyExamples] = useState([]);
  const [footer, setFooter] = useState('');
  const [buttons, setButtons] = useState([]);
  const [prefill, setPrefill] = useState({ header: [], body: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const headerVars = countVars(header);
  const bodyVars = countVars(body);
  const examples = Array.from({ length: bodyVars }, (_, i) => bodyExamples[i] || '');

  const components = useMemo(() => {
    const c = [];
    if (header) c.push({ type: 'HEADER', format: 'TEXT', text: header });
    c.push({ type: 'BODY', text: body });
    if (footer) c.push({ type: 'FOOTER', text: footer });
    if (buttons.length) c.push({ type: 'BUTTONS', buttons: buttons.map(text => ({ type: 'QUICK_REPLY', text })) });
    return c;
  }, [header, body, footer, buttons]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const def = {
        name, language, category,
        ...(header ? { header: { text: header, ...(headerVars ? { example: headerExample } : {}) } } : {}),
        body: { text: body, examples },
        ...(footer ? { footer: { text: footer } } : {}),
        ...(buttons.length ? { buttons: buttons.map(text => ({ text })) } : {}),
        ...(options.prefill_enabled ? { prefill: {
          header: Array.from({ length: headerVars }, (_, i) => prefill.header[i] || null),
          body: Array.from({ length: bodyVars }, (_, i) => prefill.body[i] || null),
        } } : {}),
      };
      const t = await apiFetch('/api/templates', { method: 'POST', body: JSON.stringify(def) });
      onCreated(t);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const prefillSelect = (part, i) => options.prefill_enabled ? (
    <select style={s.select} value={prefill[part][i] || ''} title="Prellenar con un dato del lead en Zoho (V2)"
      onChange={e => setPrefill(p => { const n = { ...p, [part]: [...p[part]] }; n[part][i] = e.target.value || null; return n; })}>
      <option value="">Sin prellenar</option>
      {options.zoho_fields.map(f => <option key={f} value={f}>Zoho: {f}</option>)}
    </select>
  ) : null;

  return (
    <form style={s.editor} onSubmit={submit}>
      <div style={s.editorCols}>
        <div style={s.fields}>
          <h3 style={s.h3}>Nueva plantilla</h3>
          <label style={s.label}>Nombre (minúsculas, números y _)
            <input style={s.input} value={name} onChange={e => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="seguimiento_visita" required />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ ...s.label, flex: 1 }}>Idioma
              <select style={s.select} value={language} onChange={e => setLanguage(e.target.value)}>
                {options.languages.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>
            <label style={{ ...s.label, flex: 1 }}>Categoría
              <select style={s.select} value={category} onChange={e => setCategory(e.target.value)}>
                {options.categories.map(c => <option key={c} value={c}>{c === 'UTILITY' ? 'UTILITY (servicio)' : 'MARKETING (promoción)'}</option>)}
              </select>
            </label>
          </div>
          <label style={s.label}>Encabezado (opcional, máx. 60, una variable {'{{1}}'})
            <input style={s.input} value={header} maxLength={60} onChange={e => setHeader(e.target.value)} placeholder="Hola {{1}}" />
          </label>
          {headerVars > 0 && (
            <div style={s.varRow}>
              <input style={s.input} value={headerExample} onChange={e => setHeaderExample(e.target.value)} placeholder="Ejemplo de {{1}} (ej. Ana)" required />
              {prefillSelect('header', 0)}
            </div>
          )}
          <label style={s.label}>Cuerpo (máx. 1024; variables {'{{1}}'}, {'{{2}}'}…; no puede empezar ni terminar con una)
            <textarea style={{ ...s.input, minHeight: 90, resize: 'vertical' }} value={body} maxLength={1024}
              onChange={e => setBody(e.target.value)} placeholder="Te esperamos el {{1}} a las {{2}} en la escuela." required />
          </label>
          {examples.map((ex, i) => (
            <div key={i} style={s.varRow}>
              <input style={s.input} value={ex} placeholder={`Ejemplo de {{${i + 1}}}`} required
                onChange={e => setBodyExamples(prev => { const n = [...prev]; n[i] = e.target.value; return n; })} />
              {prefillSelect('body', i)}
            </div>
          ))}
          <label style={s.label}>Pie (opcional, máx. 60, sin variables)
            <input style={s.input} value={footer} maxLength={60} onChange={e => setFooter(e.target.value)} />
          </label>
          <div style={s.label}>Botones de respuesta rápida (máx. 3)
            {buttons.map((b, i) => (
              <div key={i} style={s.varRow}>
                <input style={s.input} value={b} maxLength={25} required
                  onChange={e => setButtons(prev => prev.map((x, j) => (j === i ? e.target.value : x)))} />
                <button type="button" style={s.btn} onClick={() => setButtons(prev => prev.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            {buttons.length < 3 && <button type="button" style={{ ...s.btn, alignSelf: 'flex-start' }} onClick={() => setButtons(p => [...p, ''])}>+ Botón</button>}
          </div>
        </div>
        <div style={s.previewCol}>
          <div style={s.muted}>Vista previa (con los ejemplos)</div>
          <TemplatePreview components={components} vars={{ header: [headerExample], body: examples }} />
          <div style={s.muted}>Meta revisa cada plantilla (de minutos a horas) y cobra los envíos según la categoría.</div>
        </div>
      </div>
      {error && <div style={s.errorBox}>⚠️ {error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" style={s.primaryBtn} disabled={saving}>{saving ? 'Enviando a Meta…' : 'Enviar a revisión de Meta'}</button>
        <button type="button" style={s.btn} onClick={onCancel}>Cancelar</button>
      </div>
    </form>
  );
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 14 },
  toolbar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  primaryBtn: { background: 'var(--accent-gradient)', border: 'none', borderRadius: 8, padding: '8px 14px', color: 'var(--on-accent)', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  btn: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '7px 12px', color: 'var(--text)', fontSize: 12, cursor: 'pointer' },
  dangerBtn: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6, padding: '5px 10px', color: 'var(--danger)', cursor: 'pointer', fontSize: 12 },
  muted: { color: 'var(--text-3)', fontSize: 12 },
  okBox: { background: 'var(--success-soft)', border: '1px solid var(--success-border)', borderRadius: 10, padding: '10px 14px', color: 'var(--success)', fontSize: 13 },
  errorBox: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 10, padding: '10px 14px', color: 'var(--danger)', fontSize: 13 },
  tableWrap: { overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', color: 'var(--text-2)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '1px solid var(--border)' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  chip: { fontSize: 11, fontWeight: 700, border: '1px solid', borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' },
  reason: { fontSize: 11, color: 'var(--danger)', marginTop: 4 },
  editor: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 },
  editorCols: { display: 'flex', gap: 20, flexWrap: 'wrap' },
  fields: { flex: '1 1 380px', display: 'flex', flexDirection: 'column', gap: 10 },
  previewCol: { flex: '0 1 340px', display: 'flex', flexDirection: 'column', gap: 8 },
  h3: { margin: 0, fontSize: 15, color: 'var(--text)' },
  label: { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: 'var(--text-2)' },
  input: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '8px 10px', color: 'var(--text)', fontSize: 13, outline: 'none', flex: 1, fontFamily: 'inherit' },
  select: { background: 'var(--raised)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '7px 10px', color: 'var(--text)', fontSize: 13 },
  varRow: { display: 'flex', gap: 6, alignItems: 'center' },
};
